import { MissionSection } from "components/missionDetail/MissionDetailParts";
import { languageIcon, licenceIcon, toolIcon } from "components/missionDetail/missionDetailIcons";

const toNamedItems = (values) => (values ?? []).filter(Boolean).map((name) => ({ name, providedBy: null }));

/**
 * The tools with their provider. `missionTools` is computed by the api; until it is deployed,
 * fall back on the bare names of `tools`, with no provider.
 */
const getMissionTools = (mission) => {
    if (Array.isArray(mission?.missionTools)) {
        return mission.missionTools
            .filter((tool) => typeof tool?.name === "string" && tool.name.trim())
            .map((tool) => ({ name: tool.name, providedBy: tool.providedBy ?? null }));
    }
    return toNamedItems(mission?.tools);
};

/** Requirement families and the icon of each item, in the app order. */
const FAMILIES = [
    { key: "tools", iconOf: toolIcon, itemsOf: getMissionTools },
    { key: "licences", iconOf: licenceIcon, itemsOf: (mission) => toNamedItems(mission?.licences) },
    { key: "language", iconOf: languageIcon, itemsOf: (mission) => toNamedItems(mission?.language) },
];

/**
 * The job requirements by family (equipment / PPE, licences, languages), as in the temp worker
 * app (MissionRequirements): a title, "Requis pour cette mission", then one line per item with
 * its icon. A tool shows its provider only when it is known: an unknown one shows nothing rather
 * than a guess.
 */
const MissionRequirements = ({ mission, labels }) => {
    const families = FAMILIES.map((family) => ({ ...family, items: family.itemsOf(mission) })).filter(
        (family) => family.items.length
    );
    if (!families.length) return null;

    return (
        <MissionSection>
            <div className="flex flex-col gap-6">
                {families.map(({ key, iconOf, items }) => (
                    <div key={key}>
                        <h2 className="text-sm font-semibold leading-5 text-gray-900">{labels[key]}</h2>
                        <p className="text-sm leading-5 text-gray-500">{labels.required}</p>
                        <ul className="mt-3 flex flex-col gap-3">
                            {items.map(({ name, providedBy }, index) => {
                                const Icon = iconOf(name);
                                const providerLabel = providedBy ? labels.toolProvidedBy?.[providedBy] : null;
                                return (
                                    <li key={`${name}-${index}`} className="flex min-h-[26px] items-center gap-3">
                                        <Icon size={20} color="#1f2937" strokeWidth={2} aria-hidden="true" />
                                        <div className="flex min-w-0 flex-1 flex-col">
                                            <span className="text-sm leading-5 text-gray-900">{name}</span>
                                            {providerLabel ? (
                                                <span className="text-sm leading-5 text-gray-500">{providerLabel}</span>
                                            ) : null}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                ))}
            </div>
        </MissionSection>
    );
};

export default MissionRequirements;
